import { useEffect, useRef, useState, useCallback } from 'react';

const BUFFER_SIZE = 2048; // Size of audio chunks to process
const MAX_QUEUE_DEPTH = 4; // ~85ms max latency @ 48kHz

export function useAudio() {
  const [isMicrophoneActive, setIsMicrophoneActive] = useState(false);
  const [isSpeakerActive, setIsSpeakerActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const playbackNodeRef = useRef<ScriptProcessorNode | null>(null);

  const playbackQueueRef = useRef<Float32Array[]>([]);
  const onAudioCaptureRef = useRef<((samples: Float32Array) => void) | null>(null);

  // Initialize audio context
  useEffect(() => {
    audioContextRef.current = new AudioContext();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Setup microphone processor
  useEffect(() => {
    if (!micStreamRef.current || !audioContextRef.current) return;

    const audioContext = audioContextRef.current;
    const source = audioContext.createMediaStreamSource(micStreamRef.current);
    const processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);

    processor.onaudioprocess = (e) => {
      // Capture audio immediately when available
      const inputData = e.inputBuffer.getChannelData(0);
      const samples = new Float32Array(inputData);

      // Call the callback if set
      if (onAudioCaptureRef.current) {
        onAudioCaptureRef.current(samples);
      }
    };

    source.connect(processor);
    processor.connect(audioContext.destination);
    scriptProcessorRef.current = processor;

    return () => {
      processor.disconnect();
      source.disconnect();
    };
  }, [isMicrophoneActive]);

  // Start microphone
  const startMicrophone = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      if (audioContextRef.current) {
        // Resume if suspended
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        setIsMicrophoneActive(true);
        console.log('[Audio] Microphone started, sample rate:', audioContextRef.current.sampleRate);
      }
    } catch (err) {
      setError(`Microphone error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Microphone error:', err);
    }
  }, []);

  // Stop microphone
  const stopMicrophone = useCallback(() => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    setIsMicrophoneActive(false);
    scriptProcessorRef.current = null;
  }, []);

  // Initialize playback
  useEffect(() => {
    if (!isSpeakerActive || !audioContextRef.current) {
      if (playbackNodeRef.current) {
        playbackNodeRef.current.disconnect();
        playbackNodeRef.current = null;
      }
      playbackQueueRef.current = [];
      return;
    }

    const audioContext = audioContextRef.current;
    const playbackNode = audioContext.createScriptProcessor(BUFFER_SIZE, 0, 1);

    let currentBuffer: Float32Array | null = null;
    let offset = 0;

    playbackNode.onaudioprocess = (e) => {
      const outputData = e.outputBuffer.getChannelData(0);

      for (let i = 0; i < outputData.length; i++) {
        // Get next buffer if needed
        if (!currentBuffer || offset >= currentBuffer.length) {
          currentBuffer = playbackQueueRef.current.shift() || null;
          offset = 0;
        }

        // Write sample or silence
        outputData[i] = currentBuffer ? currentBuffer[offset++] : 0;
      }
    };

    playbackNode.connect(audioContext.destination);
    playbackNodeRef.current = playbackNode;

    return () => {
      playbackNode.disconnect();
      playbackQueueRef.current = [];
    };
  }, [isSpeakerActive]);

  // Play audio - just queue it
  const playAudio = useCallback((samples: Float32Array) => {
    if (!isSpeakerActive) return;

    // Drop oldest if queue full (bounded FIFO)
    if (playbackQueueRef.current.length >= MAX_QUEUE_DEPTH) {
      playbackQueueRef.current.shift(); // Remove oldest
    }

    playbackQueueRef.current.push(new Float32Array(samples));
  }, [isSpeakerActive]);

  // Toggle speaker
  const toggleSpeaker = useCallback(() => {
    setIsSpeakerActive(prev => !prev);
  }, []);

  // Set callback for audio capture (called on each audio buffer)
  const setOnAudioCapture = useCallback((callback: ((samples: Float32Array) => void) | null) => {
    onAudioCaptureRef.current = callback;
  }, []);

  return {
    isMicrophoneActive,
    isSpeakerActive,
    error,
    startMicrophone,
    stopMicrophone,
    toggleSpeaker,
    setOnAudioCapture,
    playAudio,
    sampleSize: BUFFER_SIZE,
  };
}
